import { useEffect, useState } from 'react';
import {
  fetchProjectById,
  Project,
  rereadProjectIfReading,
  setProjectCustomFields,
  setRelatedInventoryCustomFields
} from '../../../store/Project';
import { RelatedInventoryRiskItem } from '../../../Api/Projects/projectsApi';
import { TextSpan } from '../../Atoms/Text';
import { CustomFieldDetailRow } from '../../Compounds/DetailsDrawer/CustomFieldDetailRow';
import {
  CUSTOM_FIELD_COLUMN_PREFIX,
  savedDateMovesRisk,
  upsertCustomFieldValue
} from '../../Compounds/UpstackDataGrid/customFieldColumns';
import { fetchCustomFieldDefinitionsApi } from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { upsertCustomFieldValuesApi } from '../../../Api/Admin/CustomFields/customFieldValuesApi';
import { CustomFieldDefinition } from '../../../types';
import { getUser, isCapabilityDenied, isInternalUser } from '../../../store/User';
import {
  AccountGridKey,
  configAccountIdFor,
  isCardFieldVisible,
  useAccountGridConfig,
  useColumnsTurnedOn
} from '../../../store/AccountCustomization';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { recordCustomFieldEditable } from '../../../utils/customFieldEditability';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';

export interface ProjectCustomFieldsSectionProps {
  project: Project;
  asset?: RelatedInventoryRiskItem;
  heading?: string;
  className?: string;
}

export const ProjectCustomFieldsSection = ({
  project,
  asset,
  heading = 'Custom Fields',
  className = ''
}: ProjectCustomFieldsSectionProps) => {
  const { setSnack } = useSnackbar();
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);

  // Scope follows the card: the single-asset card edits the asset's ProjectDetail fields (config key
  // related_project_assets_grid); the multi-asset card edits the project's Project fields (config key
  // project_grid) — mirroring each grid's custom-field columns.
  const entityType: 'Project' | 'ProjectDetail' = asset ? 'ProjectDetail' : 'Project';
  const baseKey: AccountGridKey = asset ? 'related_project_assets_grid' : 'project_grid';
  const targetId = asset ? asset.id : project.id;
  const targetCustomFields = asset ? asset.custom_fields : project.custom_fields;

  const accountId = configAccountIdFor(project.account?.id, asset);

  // Custom fields follow the same account column-visibility config as the card's native fields.
  const gridConfig = useAccountGridConfig(accountId, baseKey);
  const turnedOn = useColumnsTurnedOn(baseKey, gridConfig);

  useEffect(() => {
    if (!accountId) {
      setCustomFieldDefs([]);
      return;
    }
    fetchCustomFieldDefinitionsApi(accountId, undefined, entityType).then(({ data: defs }) => {
      // Clear on a failed/empty response so a prior entity's fields don't linger.
      setCustomFieldDefs((defs ?? []).filter((d) => !d.is_archived).sort((a, b) => a.position - b.position));
    });
  }, [accountId, entityType]);

  const handleCustomFieldSave = async (definitionId: string, value: string) => {
    if (!targetId) return;
    const { error } = await upsertCustomFieldValuesApi(targetId, entityType, [
      { custom_field_definition_id: definitionId, value }
    ]);
    if (error) {
      setSnack({ message: 'Failed to save custom field.', open: true, type: 'error' });
      throw new Error(error.message);
    }
    const def = customFieldDefs.find((d) => d.id === definitionId);
    const nextCustomFields = upsertCustomFieldValue(
      targetCustomFields,
      {
        id: definitionId,
        name: def?.name ?? '',
        field_type: def?.field_type ?? 'text',
        position: def?.position ?? 0,
        is_editable: def?.is_editable
      },
      value
    );
    // Asset scope updates the matching related-inventory row; project scope updates the project itself.
    if (asset) {
      setRelatedInventoryCustomFields(project.id, asset.id, nextCustomFields);
      if (def && savedDateMovesRisk(def)) void fetchProjectById(project.id, true);
      else void rereadProjectIfReading(project.id, true);
    } else {
      setProjectCustomFields(project.id, nextCustomFields);
    }
  };

  const visibleDefs = customFieldDefs.filter((def) =>
    isCardFieldVisible(`${CUSTOM_FIELD_COLUMN_PREFIX}${def.id}`, false, gridConfig, turnedOn)
  );

  const unrestricted = isInternalUser(getUser());
  const denied = isCapabilityDenied(CustomFieldCapability.VALUES_UPSERT, getUser());
  const fieldReadOnly = (def: CustomFieldDefinition) =>
    !recordCustomFieldEditable(targetCustomFields, def, unrestricted, denied);

  if (visibleDefs.length === 0) return null;

  return (
    <div className={className}>
      <TextSpan
        weight="semibold"
        size="sm14">
        {heading}
      </TextSpan>
      <div className="mt-4">
        {visibleDefs.map((def) => (
          <CustomFieldDetailRow
            key={def.id}
            definition={def}
            value={targetCustomFields?.find((cf) => cf.definition_id === def.id)?.value ?? ''}
            onSave={handleCustomFieldSave}
            readOnly={fieldReadOnly(def)}
          />
        ))}
      </div>
    </div>
  );
};
